import React, { useEffect, useRef, useState } from 'react';

// "Placement" tab of the admin panel: pick an object of the catalog scanned by
// the server (crafting stations, furniture, containers, lights, decor,
// activators) and place it in the world, or review / visit / remove what was
// already placed. Everything goes through the panel's own messages
// (adminMenuService.ts relays them to placementSystem.ts) and comes back as
// DOM events the client forwards, same pattern as the weather tab.

interface PlaceableResult {
  id: number;
  n: string; // display name, editor id when the record has none
  edid: string;
  t: string; // record type
  c: string; // category id
  mod: string; // plugin
}

interface ResultsDetail {
  ready: boolean;
  query: string;
  category: string;
  total: number;
  counts: Record<string, number>;
  results: PlaceableResult[];
}

interface PlacedEntry {
  r: number; // reference id
  n: string;
  c: string;
  pos: number[];
  by: string;
  at: number;
}

interface PlacedDetail {
  total: number;
  objects: PlacedEntry[];
}

interface HiddenEntry {
  r: number; // reference id
  n: string; // the name the server gives (display name, else editor id)
  edid?: string;
  mod?: string; // plugin of the base record
  ft: number;
  c: string; // cell or world
  pos: number[];
  by: string;
  at: number;
}

interface HiddenDetail {
  total: number;
  objects: HiddenEntry[];
}

const CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'craft', label: 'Postes de craft' },
  { id: 'furniture', label: 'Meubles' },
  { id: 'container', label: 'Conteneurs' },
  { id: 'light', label: 'Lumières' },
  { id: 'decor', label: 'Décor' },
  { id: 'activator', label: 'Activateurs' },
];

const categoryLabel = (id: string): string => CATEGORIES.find((c) => c.id === id)?.label ?? id;

const SEARCH_DEBOUNCE_MS = 250;
// While the server is still scanning its plugins at boot, the panel asks again
const NOT_READY_RETRY_MS = 2000;

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('placementTab sendMessage', payload);
  }
};

const formatDate = (epochMs: number): string => {
  if (!epochMs) return '';
  try {
    return new Date(epochMs).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '';
  }
};

export const PlacementTab: React.FC = () => {
  const [view, setView] = useState<'catalog' | 'placed' | 'hidden'>('catalog');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [results, setResults] = useState<ResultsDetail | null>(null);
  const [placed, setPlaced] = useState<PlacedDetail | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);
  const [hidden, setHidden] = useState<HiddenDetail | null>(null);
  const [confirmRestore, setConfirmRestore] = useState<number | null>(null);
  const [refInput, setRefInput] = useState('');
  const retry = useRef<number | undefined>(undefined);

  // Listen to the server's answers, and fetch both lists when the tab opens
  useEffect(() => {
    const onResults = (e: Event) => setResults((e as CustomEvent).detail as ResultsDetail);
    const onPlaced = (e: Event) => {
      setPlaced((e as CustomEvent).detail as PlacedDetail);
      setConfirmRemove(null);
    };
    window.addEventListener('skymp5-client:placementResults', onResults);
    window.addEventListener('skymp5-client:placementList', onPlaced);
    const onHidden = (e: Event) => {
      setHidden((e as CustomEvent).detail as HiddenDetail);
      setConfirmRestore(null);
    };
    window.addEventListener('skymp5-client:mapHideList', onHidden);
    send({ action: 'placementList' });
    send({ action: 'mapHideList' });
    return () => {
      window.removeEventListener('skymp5-client:mapHideList', onHidden);
      window.removeEventListener('skymp5-client:placementResults', onResults);
      window.removeEventListener('skymp5-client:placementList', onPlaced);
      window.clearTimeout(retry.current);
    };
  }, []);

  // Search on every change of the query or the category, after a short pause
  // while typing; the first run (empty query) loads the initial list
  useEffect(() => {
    const t = window.setTimeout(() => {
      send({ action: 'placementSearch', query, category });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, category]);

  // The catalog is built in the background at server start: ask again until it is ready
  useEffect(() => {
    window.clearTimeout(retry.current);
    if (results && !results.ready) {
      retry.current = window.setTimeout(() => send({ action: 'placementSearch', query, category }), NOT_READY_RETRY_MS);
    }
    return () => window.clearTimeout(retry.current);
  }, [results]);

  const counts = results?.counts ?? {};
  const allCount = Object.keys(counts).reduce((n, k) => n + (counts[k] || 0), 0);

  return (
    <div className="admin-panel__body">
      <div className="admin-panel__chips">
        <button
          type="button"
          className={'admin-panel__chip' + (view === 'catalog' ? ' admin-panel__chip--active' : '')}
          onClick={() => setView('catalog')}
        >
          Catalogue
        </button>
        <button
          type="button"
          className={'admin-panel__chip' + (view === 'placed' ? ' admin-panel__chip--active' : '')}
          onClick={() => {
            setView('placed');
            send({ action: 'placementList' });
          }}
        >
          Objets posés{placed ? ` (${placed.total})` : ''}
        </button>
        <button
          type="button"
          className={'admin-panel__chip' + (view === 'hidden' ? ' admin-panel__chip--active' : '')}
          onClick={() => {
            setView('hidden');
            send({ action: 'mapHideList' });
          }}
        >
          Carte : objets masqués{hidden ? ` (${hidden.total})` : ''}
        </button>
      </div>

      {view === 'catalog' ? (
        <>
          <div className="admin-panel__filters">
            <input
              className="admin-panel__search"
              placeholder="Rechercher (nom ou identifiant : forge, chest, bed, candle...)"
              value={query}
              maxLength={60}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <div className="admin-panel__chips">
            <button
              type="button"
              className={'admin-panel__chip' + (category === 'all' ? ' admin-panel__chip--active' : '')}
              onClick={() => setCategory('all')}
            >
              Tous{results ? ` (${allCount})` : ''}
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={'admin-panel__chip' + (category === c.id ? ' admin-panel__chip--active' : '')}
                onClick={() => setCategory(c.id)}
              >
                {c.label}{results ? ` (${counts[c.id] || 0})` : ''}
              </button>
            ))}
          </div>

          <div className="admin-panel__filters">
            <button
              type="button"
              className="admin-panel__button admin-panel__button--primary"
              title="Ferme le panel : ouvre la console (~), clique sur un objet du monde, referme la console, puis place un exemplaire neuf du même objet"
              onClick={() => send({ action: 'mapCopyStart' })}
            >
              Retrouver un objet du monde (console)
            </button>
          </div>

          <div className="admin-panel__hint">
            « Poser » ferme le panel et affiche un aperçu devant toi : les flèches pour déplacer l'objet, Page haut et
            Page bas pour le monter et le descendre, Début et Fin pour le tourner, Ctrl droit pour la précision,
            Entrée pour valider, Échap ou Retour arrière pour annuler. Tu peux marcher normalement pendant ce temps.
          </div>

          <div className="admin-panel__list">
            {!results ? (
              <div className="admin-panel__empty">Chargement...</div>
            ) : !results.ready ? (
              <div className="admin-panel__empty">Le catalogue se construit au démarrage du serveur, quelques secondes...</div>
            ) : results.results.length === 0 ? (
              <div className="admin-panel__empty">Aucun objet ne correspond.</div>
            ) : (
              <>
                {results.results.map((o) => (
                  <div key={o.id} className="admin-panel__row admin-panel__row--location">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="admin-panel__cell admin-panel__cell--name">{o.n}</div>
                      <div className="admin-panel__cell admin-panel__cell--discord-id" style={{ width: 'auto' }}>
                        <span className="admin-panel__badge">{categoryLabel(o.c)}</span>{' '}
                        {o.edid !== o.n ? `${o.edid} - ` : ''}{o.mod}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="admin-panel__button admin-panel__button--primary"
                      onClick={() => send({ action: 'placementStart', id: o.id })}
                    >
                      Poser
                    </button>
                  </div>
                ))}
                {results.total > results.results.length ? (
                  <div className="admin-panel__empty">
                    {results.results.length} affichés sur {results.total} : précise la recherche pour voir les autres.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : view === 'hidden' ? (
        <>
          <div className="admin-panel__hint">
            Retire un objet du décor du jeu (arbre, rocher, mur, accessoire, extérieur comme intérieur). Ouvre la
            console du jeu (~) et clique sur l'objet : le moteur le choisit, donc c'est exact, et la console affiche son
            RefID en haut (par exemple 32023821). Recopie-le ici : le panel se ferme et une fenêtre donne le nom, le type
            et la distance de l'objet ; Suppr confirme, Retour arrière annule. Avec le mode « Édition de la carte »
            (onglet Modes), ALT propose aussi « Masquer de la carte » pour le dernier objet cliqué dans la console.
            L'objet disparaît pour tous les joueurs, collision comprise, et peut être remis ici. Le relief, l'eau et
            l'aspect de loin des grands éléments ne changent pas.
          </div>
          <div className="admin-panel__filters">
            <input
              className="admin-panel__search"
              placeholder="RefID de la console (ex. 32023821)"
              value={refInput}
              maxLength={10}
              onChange={(e) => setRefInput(e.target.value.replace(/[^0-9a-fA-Fx]/g, ''))}
            />
            <button
              type="button"
              className="admin-panel__button admin-panel__button--primary"
              disabled={refInput.replace(/^0x/i, '').length === 0}
              onClick={() => {
                send({ action: 'mapHideById', refId: refInput });
                setRefInput('');
              }}
            >
              Masquer cet objet
            </button>
            <button
              type="button"
              className="admin-panel__button"
              onClick={() => send({ action: 'mapHideStart' })}
              title="Ferme le panel et attend un clic dans la console"
            >
              Attendre un clic en console
            </button>
          </div>
          <div className="admin-panel__list">
            {!hidden || hidden.objects.length === 0 ? (
              <div className="admin-panel__empty">Aucun objet masqué.</div>
            ) : (
              <>
                {hidden.objects.map((o) => (
                  <div key={o.r} className="admin-panel__row admin-panel__row--location">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="admin-panel__cell admin-panel__cell--name">{o.n}</div>
                      <div className="admin-panel__cell admin-panel__cell--discord-id" style={{ width: 'auto' }}>
                        {o.edid && o.edid !== o.n ? `${o.edid} - ` : ''}{o.mod ? `${o.mod} - ` : ''}{o.r.toString(16)} - {o.by}{o.at ? ` - ${formatDate(o.at)}` : ''} - [{o.pos.join(', ')}]
                      </div>
                    </div>
                    <button
                      type="button"
                      className="admin-panel__button"
                      onClick={() => send({ action: 'mapHideGoto', refId: o.r })}
                    >
                      Aller
                    </button>
                    {confirmRestore === o.r ? (
                      <>
                        <button
                          type="button"
                          className="admin-panel__button admin-panel__button--danger"
                          onClick={() => {
                            send({ action: 'mapHideRestore', refId: o.r });
                            setConfirmRestore(null);
                          }}
                        >
                          Confirmer
                        </button>
                        <button
                          type="button"
                          className="admin-panel__button admin-panel__button--secondary"
                          onClick={() => setConfirmRestore(null)}
                        >
                          Annuler
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="admin-panel__button"
                        onClick={() => setConfirmRestore(o.r)}
                      >
                        Remettre
                      </button>
                    )}
                  </div>
                ))}
                {hidden.total > hidden.objects.length ? (
                  <div className="admin-panel__empty">
                    Les {hidden.objects.length} plus récents sur {hidden.total}.
                  </div>
                ) : null}
              </>
            )}
          </div>
        </>
      ) : (
        <div className="admin-panel__list">
          {!placed || placed.objects.length === 0 ? (
            <div className="admin-panel__empty">Aucun objet posé avec cet outil.</div>
          ) : (
            <>
              {placed.objects.map((o) => (
                <div key={o.r} className="admin-panel__row admin-panel__row--location">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="admin-panel__cell admin-panel__cell--name">{o.n}</div>
                    <div className="admin-panel__cell admin-panel__cell--discord-id" style={{ width: 'auto' }}>
                      <span className="admin-panel__badge">{categoryLabel(o.c)}</span>{' '}
                      {o.by}{o.at ? ` - ${formatDate(o.at)}` : ''} - [{o.pos.join(', ')}]
                    </div>
                  </div>
                  <button
                    type="button"
                    className="admin-panel__button"
                    onClick={() => send({ action: 'placementGoto', refrId: o.r })}
                  >
                    Aller
                  </button>
                  <button
                    type="button"
                    className="admin-panel__button admin-panel__button--primary"
                    title="Ferme le panel et affiche l'objet pour le déplacer (mêmes touches que pour poser)"
                    onClick={() => send({ action: 'placementMove', refrId: o.r })}
                  >
                    Déplacer
                  </button>
                  {confirmRemove === o.r ? (
                    <>
                      <button
                        type="button"
                        className="admin-panel__button admin-panel__button--danger"
                        onClick={() => {
                          send({ action: 'placementRemove', refrId: o.r });
                          setConfirmRemove(null);
                        }}
                      >
                        Confirmer
                      </button>
                      <button
                        type="button"
                        className="admin-panel__button admin-panel__button--secondary"
                        onClick={() => setConfirmRemove(null)}
                      >
                        Annuler
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="admin-panel__button admin-panel__button--danger"
                      onClick={() => setConfirmRemove(o.r)}
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              ))}
              {placed.total > placed.objects.length ? (
                <div className="admin-panel__empty">
                  Les {placed.objects.length} plus récents sur {placed.total}.
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
};
